import type { CarSnapshot } from '../sim/types';
import type { EvaluatorUiState } from '../levels/Evaluator';

export interface Toast {
  id: number;
  key: string;
  bornTick: number;
}

export interface HudState {
  snap: CarSnapshot | null;
  evalUi: EvaluatorUiState | null;
  toasts: Toast[];
  paused: boolean;
  camera: 'chase' | 'cockpit';
}

const TOAST_TTL_TICKS = 210; // ~3,5 s
const EMIT_EVERY_TICKS = 4; // HUD a ~15 Hz

/**
 * Puente sim → UI: el bucle de juego empuja snapshots a 60 Hz y el HUD
 * se suscribe a una versión limitada a ~15 Hz para no saturar React.
 */
export class SimBridge {
  private listeners = new Set<(s: HudState) => void>();
  private state: HudState = { snap: null, evalUi: null, toasts: [], paused: false, camera: 'chase' };
  private nextToastId = 1;
  private lastEmitTick = -999;

  push(snap: CarSnapshot, evalUi: EvaluatorUiState | null, warningKeys: string[]): void {
    this.state.snap = snap;
    this.state.evalUi = evalUi;
    for (const key of warningKeys) {
      // Evita duplicar el mismo aviso si aún está visible
      if (!this.state.toasts.some((t) => t.key === key && snap.tick - t.bornTick < 60)) {
        this.state.toasts.push({ id: this.nextToastId++, key, bornTick: snap.tick });
      }
    }
    this.state.toasts = this.state.toasts.filter((t) => snap.tick - t.bornTick < TOAST_TTL_TICKS);
    if (snap.tick - this.lastEmitTick >= EMIT_EVERY_TICKS) {
      this.lastEmitTick = snap.tick;
      this.emit();
    }
  }

  setPaused(paused: boolean): void {
    if (this.state.paused !== paused) {
      this.state.paused = paused;
      this.emit();
    }
  }

  setCamera(camera: 'chase' | 'cockpit'): void {
    if (this.state.camera !== camera) {
      this.state.camera = camera;
      this.emit();
    }
  }

  private emit(): void {
    const copy: HudState = { ...this.state, toasts: [...this.state.toasts] };
    for (const cb of this.listeners) cb(copy);
  }

  subscribe(cb: (s: HudState) => void): () => void {
    this.listeners.add(cb);
    cb({ ...this.state, toasts: [...this.state.toasts] });
    return () => this.listeners.delete(cb);
  }
}
